import { z } from "zod";
import { FormElement } from "./createFormStructure";
import {
  e_mail,
  phoneNumber,
  requiredNumber,
  privacy,
  requiredString,
} from "../../utils/validations";

export const generateSchema = (
  inputFields: FormElement[],
  getLabel: (key: string, fallback?: string) => string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const zObject = inputFields.reduce<Record<string, any>>((acc, el) => {
    const {
      type,
      isRequired,
      requiredEmptyError = "",
      typeErrorMessage = "",
      label,
      name,
    } = el;
    const requiredMessage = requiredEmptyError || `${label} is required`;

    switch (type) {
      case "email":
        acc[name] = e_mail(requiredMessage, typeErrorMessage);
        break;
      case "tel":
        acc[name] = phoneNumber(typeErrorMessage);
        break;
      case "number":
        acc[name] = requiredNumber(requiredMessage, typeErrorMessage);
        if (el.min) {
          acc[name] = acc[name].min(
            el.min,
            getLabel("forms_min_error").replaceAll("${min}", el.min.toString()),
          );
        }
        if (el.max) {
          acc[name] = acc[name].max(
            el.max,
            getLabel("forms_max_error").replaceAll("${max}", el.max.toString()),
          );
        }
        const step = el.step;
        if (step) {
          acc[name] = (acc[name] as z.ZodNumber).refine(
            (val) => {
              // Check if the value is at min or can be reached by adding steps from min
              return (val - (el.min ?? 0)) % step === 0;
            },
            {
              message: `step error (needs to be added to options)`,
            },
          );
        }
        break;

      case "checkbox":
        acc[name] = privacy(requiredMessage);
        break;
      case "text":
      case "url":
      case "textarea":
      case "radiobutton":
      case "datepicker":
        if (isRequired) {
          acc[name] = requiredString(requiredMessage);
        }
        break;
      case "dropwdown":
        if (isRequired) {
          if (el.multiSelect) {
            acc[name] = z
              .array(z.string(), { required_error: requiredMessage })
              .min(1, { message: requiredMessage });
          } else {
            acc[name] = z.string({ required_error: requiredMessage });
          }
        }
        break;
    }

    return acc;
  }, {});
  return z.object(zObject);
};
